import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {isAxiosError} from "axios";
import {queryHttp} from "~/query/queryHttp";
import {ApprovalStatusEnum} from "~/services/classes/AiClasses";
import {notificationStore} from "~/stores/init";
import {useStores} from "~/stores";
import {
    taxonomyApprovalApplyUrl,
    taxonomyApprovalApproveUrl,
} from "~/pages/taxonomy-builder-v2/services/taxonomyBuilderUrls";
import {taxonomyApprovalRequestQueryKey, taxonomyApprovalStateQueryKey} from "./useTaxonomyApprovalQueries";

export type FinishTaxonomyApprovalVariables = {
    decision: 'approve' | 'reject';
    /** The reply to the requester */
    feedbackNotes: string;
};

function errorMessage(error: unknown, decision: FinishTaxonomyApprovalVariables['decision']): string {
    const action = decision === 'approve' ? 'approve' : 'reject';
    if (isAxiosError(error) && error.response) {
        const data = error.response.data as {status?: string | string[], detail?: string} | undefined;
        const reason = Array.isArray(data?.status) ? data.status[0] : data?.status ?? data?.detail;
        // e.g. "still_busy" while a previous run hasn't finished
        if (reason) return `Failed to ${action} the request. ${reason}`;
    }
    return `Failed to ${action} the request. Please try again.`;
}

/**
 * Finishes the review with a reply to the requester.
 * - Approve: `apply_taxonomy_only/`, as the current approval page does: the approver's copy (with its
 *   reverts) becomes a new taxonomy and the datasets move to it. The datasets are reloaded, so the app
 *   picks up the new taxonomy.
 * - Reject: `apply/` with status `rejected`; the requester can edit the taxonomy again. The current
 *   approval page has no reject; TODO: check this with Dennis.
 */
export function useFinishTaxonomyApprovalMutation(approvalId: number) {
    const queryClient = useQueryClient();
    const {bagStore} = useStores();

    return useMutation<unknown, DefaultError, FinishTaxonomyApprovalVariables>({
        mutationFn: async ({decision, feedbackNotes}) => decision === 'approve'
            ? (await queryHttp.post(taxonomyApprovalApproveUrl(approvalId), {feedback_notes: feedbackNotes})).data
            : (await queryHttp.post(taxonomyApprovalApplyUrl(approvalId), {status: ApprovalStatusEnum.REJECTED, feedback_notes: feedbackNotes})).data,
        onSuccess: (_, {decision}) => {
            void queryClient.invalidateQueries({queryKey: taxonomyApprovalRequestQueryKey(approvalId)});
            void queryClient.invalidateQueries({queryKey: taxonomyApprovalStateQueryKey(approvalId)});
            if (decision === 'approve') {
                // The datasets now point at the new taxonomy: reload them and keep the open one selected.
                const bagId = bagStore.bag?.id;
                bagStore.getAllBags().subscribe({
                    next: bags => {
                        const bag = bags.find(b => b.id === bagId);
                        if (bag) bagStore.setBag(bag);
                    },
                });
            }
            notificationStore.show(decision === 'approve' ? 'Request approved.' : 'Request rejected.', {
                severity: 'success',
                id: `taxonomy-approval-finish-success-${approvalId}`,
                autoHideDuration: 3000,
            });
        },
        onError: (error, {decision}) => notificationStore.show(errorMessage(error, decision), {
            severity: 'error',
            id: `taxonomy-approval-finish-error-${approvalId}`,
            autoHideDuration: 6000,
        }),
    });
}
